import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useLocation, useNavigate, useParams } from 'react-router'
import classNames from 'classnames'
import * as qs from 'qs'

// evertel
import { DocumentServiceProvider } from '@evertel/service'
import { Document, WidgetsProvider } from '@evertel/schema-parser'
import { useService } from '@evertel/di'
import { DocumentEditController, DocumentACLsController, EverdocMediaUplaodErrorEdit, hasDocumentDataChanges } from '@evertel/document'
import { WriteWidgets } from './schema-widgets'
import { DocumentFooter, DocumentIcon } from './elements'
import { useUnsavedChangesGuard } from '../utils'
import { APIDataBlueUser, APIDataDocumentSchema } from '@evertel/types'
import {
    Button, Icon, Text, Row, Col, InfoBox, Badge,
    PageBody, ContentPlaceholder,
    Card, CardHeader, CardBody,
    Breadcrumb, useUI, Input,
    FloatingMenu, MenuHeader, MenuDivider, MenuItem,
    CardFooter
} from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { getSpecificDepartmentsAccess, getRole } from '@evertel/utils'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { DocumentOwnerDisplay } from './elements/DocumentOwnerDisplay'
import { FetchUserService } from '@evertel/blue-user'
import { useDocumentTitleSuffix } from '@evertel/web/core'


const DocumentEdit: React.FC = observer(() => {

    const { id: docStringId } = useParams()
    const documentId = docStringId ? parseInt(docStringId) : null

    const sessionService = useService(SessionService, [])

    const departmentsAccessController = useService(DepartmentsAccessController, [])
    const documentEditController = useService(DocumentEditController, [documentId])
    const documentACLController = useService(DocumentACLsController, [documentId])
    const fetchUserService = useService(FetchUserService, [])

    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [hasChanged, setHasChanged] = useState(false)
    const [documentName, setDocumentName] = useState('')
    const [documentNameError, setDocumentNameError] = useState(false)
    const [documentOwner, setDocumentOwner] = useState<APIDataBlueUser | null>(null)

    const { addToast } = useUI()
    const { setTitleSuffix } = useDocumentTitleSuffix()
    const navigate = useNavigate()
    const location = useLocation()

    // Prompts on ANY navigation away (links, back button, refresh) while there are unsaved changes
    const { allowNavigation } = useUnsavedChangesGuard(hasChanged)

    // Set document title suffix
    useEffect(() => {
        if (documentId) {
            setTitleSuffix(`#${documentId}`)
        }
    }, [documentId, setTitleSuffix])

    useEffect(() => {
        (async () => {
            setIsLoading(true)
            // initialize controllers
            try {
                await Promise.all([
                    documentEditController.load(documentId, 'web'),
                    documentACLController.init(documentId, sessionService.currentUserId)
                ])
                setDocumentName(documentEditController.name)

                departmentsAccessController.init(sessionService.currentUserId)

                const ownerACL = documentACLController.aclOwner?.[0]
                if (ownerACL?.principalId) {
                    const owner = await fetchUserService.fetchSpecificUserFromDocument(
                        documentId,
                        ownerACL.principalId
                    )

                    setDocumentOwner(owner)
                }

            } catch (error) {
                if (error?.status === 401 && error?.code === 'AUTHORIZATION_REQUIRED') {
                    addToast({
                        color: 'warning',
                        title: 'Notice',
                        message: `No permissions for Document ID#${documentId}`
                    })
                    navigate('/everdocs/')
                }

                console.error(error)
            }
            setIsLoading(false)
        })()
    }, [documentId, sessionService.currentUserId])

    const onSave = async (notify: { isUrgent: boolean } | null): Promise<void> => {
        setIsSaving(true)

        const ownerACL = documentACLController.aclOwner?.[0]

        try {

            //since we are saving the owner before the update call,
            //we should validate the form before that happens.
            documentEditController.validateBeforeUpdate()

            if (!documentName?.trim()) {
                setDocumentNameError(true)
                throw new Error('Document name is required')
            }

            if (ownerACL?.principalId !== documentOwner.id) {
                await documentACLController.updateOwnerACL(documentOwner.id)
            }

            await documentEditController.update(notify)

            //Make sure everything is up to date..
            await documentEditController.load(documentId, 'web')

            addToast({
                color: 'success',
                title: 'Document Saved',
                message: `Your document has been saved ${(notify) ? 'and rooms have been notified.' : ''}`
            })

            setHasChanged(false)
            handleAfterSaveNavigation()


        } catch (error: any) {
            if (error instanceof EverdocMediaUplaodErrorEdit) {
                addToast({
                    color: 'warning',
                    title: 'Be aware',
                    message: `Document edited with some media issues. \n ${error.message}`
                })

                //Make sure everything is up to date..
                await documentEditController.load(documentId, 'web')
                setHasChanged(false)

            } else {
                addToast({
                    color: 'danger',
                    title: 'Document save failed',
                    message: error.message
                })
            }
        } finally {
            setIsSaving(false)
        }

    }

    const handleAfterSaveNavigation = () => {
        // hasChanged was just set to false but hasn't re-rendered yet; skip the guard explicitly
        allowNavigation()

        if (location.state?.from) {
            // Parse the "from" URL's search params
            const [path, search] = location.state.from.split('?')
            const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })
            const previousSelectedDoc = parsedQuery.selectedDoc as string

            // If we came from viewing this same doc, go back to preserve search/filter state
            if (previousSelectedDoc === documentId?.toString()) {
                navigate(-1)
                return
            }
        }

        // In all other cases, go to document view
        navigate(`/everdocs/view/${documentId}`)
    }

    const onChangeName = (e: any) => {
        setDocumentName(e?.target?.value)
        setDocumentNameError(false)
        setHasChanged(true)
        documentEditController.setName(e?.target?.value)
    }

    const onChange = useCallback((value: Record<string, any>): void => {
        if (hasDocumentDataChanges(documentEditController.data, value)) setHasChanged(true)
        documentEditController.updateData(value)
    }, [documentEditController])

    if (!documentEditController.loaded) {
        return (
            <DocumentPlaceholder />
        )
    }

    if (!documentEditController?.document) return null

    const document = documentEditController.document

    const breadcrumbs = [
        { label: 'EverDocs', onClick: () => navigate('/everdocs') },
        { label: 'View EverDoc', onClick: () => navigate(`/everdocs/view/${documentId}`) },
        { label: 'Edit EverDoc', active: true }
    ]
    // if (location.state?.from === 'room' && location.state?.id) {
    //     breadcrumbs[0] = {label: 'Room', onClick: () => navigate(`/room/${location.state.id}`)}
    // }
    const userAccessInDocumentDept = getSpecificDepartmentsAccess(departmentsAccessController.departmentsAccess, document.departmentId)
    const userRoleInDocumentDept = getRole(userAccessInDocumentDept?.roles)

    const ownerDisplayType = userRoleInDocumentDept === 'executive' ? 'dropdown' : 'label'

    return (
        <PageBody>
            {/* <pre>{JSON.stringify(documentEditController.media.map(item => item.fileName), null, 2)}</pre>
            <pre>{JSON.stringify(documentEditController.data, null, 2)}</pre> */}
            <Breadcrumb
                items={breadcrumbs}
            />
            {(!!documentEditController.errors?.length) &&
                <InfoBox color="danger" className="mb-3 py-2">
                    <Icon name="circle-xmark" type="solid" className="me-2" />
                    <Text>
                        {`Please review the document, there ${(documentEditController.errors.length === 1) ?
                            'is 1 error' : `are ${documentEditController.errors.length} errors`}`}
                    </Text>
                </InfoBox>
            }
            <Card>
                <CardHeader className="flex-wrap gap-2 align-items-center document-name-header py-4">
                    <div className="d-flex align-items-center" style={{ minWidth: '50%' }}>
                        <DocumentIcon
                            schema={documentEditController?.documentSchema as APIDataDocumentSchema}
                            width={40}
                            height={44}
                        />
                        <div className="w-100 ms-2 position-relative">
                            <Input
                                style={{ borderColor: documentNameError ? 'red' : undefined }}
                                className={classNames(
                                    'form-control h3',
                                    { 'is-invalid': documentNameError }
                                )}
                                value={documentName}
                                required
                                onChange={onChangeName}
                                onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                            />
                            {documentNameError &&
                                <div className="invalid-feedback">
                                    Document Name is required.
                                </div>
                            }
                        </div>
                        {(document.isArchived) &&
                            <Badge
                                color="gray400"
                                className="ms-2">
                                Archived
                            </Badge>
                        }
                    </div>
                    <Col
                        valign="center"
                        style={{ minWidth: 0 }}>
                        <DocumentOwnerDisplay
                            type={ownerDisplayType}
                            documentDeptId={documentEditController?.document?.departmentId}
                            selectedOwner={documentOwner}
                            onOwnerChange={(owner) => { setDocumentOwner(owner); setHasChanged(true) }}
                        />
                    </Col>
                    <Row
                        className="ms-auto"
                        valign="end">
                        <SaveButton
                            onSave={onSave}
                            disabled={!hasChanged}
                            isArchived={document.isArchived as boolean}
                            isLoading={isLoading}
                            isSaving={isSaving}
                            documentACLController={documentACLController}
                        />
                    </Row>
                </CardHeader>
                <CardBody>
                    <DocumentServiceProvider services={[{
                        id: 'meta', value: { media: document?.media }
                    }]}>
                        <WidgetsProvider value={WriteWidgets}>
                            <Document
                                // this must reload after save (updatedDate changes) or
                                // the media values aren't correct
                                key={String(document.updatedDate)+String(documentId)}
                                layout="full"
                                schema={documentEditController.documentSchema?.schema}
                                schemaId={documentEditController.documentSchemaId}
                                documentId={documentId}
                                value={documentEditController.data}
                                onChange={onChange}
                                errors={documentEditController.errors}
                            />
                        </WidgetsProvider>
                    </DocumentServiceProvider>
                </CardBody>
                <CardFooter className="d-flex flex-wrap align-items-center justify-content-between">
                    <div>
                        <span aria-hidden="true"><Icon size="xs" name="asterisk" color="danger" faProps={{title: 'Required'}}/> = required</span>
                    </div>
                    <DocumentFooter
                        document={document}
                        schema={documentEditController.documentSchema}
                    />
                </CardFooter>
            </Card>
        </PageBody>
    )
})

const DocumentPlaceholder = () => {
    const navigate = useNavigate()

    const breadcrumbs = [
        { label: 'EverDocs', onClick: () => navigate('/everdocs') },
        { label: `Loading Document...`, active: true }
    ]

    return (
        <PageBody>
            <Breadcrumb
                items={breadcrumbs}
            />
            <Card>
                <CardHeader className="d-block">

                    {/* Status and Title row */}
                    <div className="row">
                        <div className="col-5 flex-row">
                            <ContentPlaceholder width="50px" height={38} className="me-3" />
                            <ContentPlaceholder width="100%" height={48} />
                        </div>
                        <div className="col-3">
                        </div>
                        <div className="col-2">
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                        <div className="col-2">
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                    </div>

                </CardHeader>
                <CardBody>
                    <div className="row mb-3">
                        <div className="col-6">
                            <ContentPlaceholder width="20%" height={16} className="mb-2" />
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                        <div className="col-6">
                            <ContentPlaceholder width="30%" height={16} className="mb-2" />
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-6">
                            <ContentPlaceholder width="20%" height={16} className="mb-2" />
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                        <div className="col-6">
                            <ContentPlaceholder width="30%" height={16} className="mb-2" />
                            <ContentPlaceholder width="100%" height={38} />
                        </div>
                    </div>

                    <div className="mb-3">
                        <ContentPlaceholder width="15%" height={16} className="mb-2" />
                        <ContentPlaceholder width="100%" height={100} />
                    </div>
                </CardBody>
            </Card>
        </PageBody>
    )
}

interface SaveButtonProps {
    onSave: (notify: { isUrgent: boolean } | null) => void
    isLoading: boolean
    isSaving: boolean
    isArchived: boolean
    disabled?: boolean
    documentACLController: DocumentACLsController
}

const SaveButton: React.FC<SaveButtonProps> = ({
    onSave,
    isLoading,
    isSaving,
    isArchived,
    disabled,
    documentACLController
}) => {

    // if not shared with any rooms, no need to prompt to notify
    if (!documentACLController.aclsNotOwner?.length) {
        return (
            <div className="d-flex align-items-center ms-2">
                <Button
                    color="primary"
                    className="fw-bold"
                    onClick={() => onSave(null)}
                    disabled={isLoading || isSaving || disabled}
                    loading={isSaving}
                >
                    Save
                </Button>
            </div>
        )
    }

    return (
        <div className="d-flex align-items-center ms-2">
            <FloatingMenu
                label="Save & Update"
                disabled={isLoading || disabled}
                renderLabel={() => (
                    <>
                        Save
                        <Icon type="solid" name="caret-down" className="ms-2" />
                    </>
                )}
                className="btn btn-success"
            >
                <MenuHeader label="" style={{ textTransform: 'none', fontSize: '0.9em' }}>
                    Would you like to send notifications of this <br />
                    update to rooms this document is shared with?
                </MenuHeader>

                <MenuItem
                    label="Save (don't notify)"
                    onClick={() => onSave(null)}
                    className="text-success"
                    icon={{
                        name: 'bell-slash',
                        color: 'success'
                    }}
                />
                <MenuDivider />
                <MenuItem
                    label="Save & Notify"
                    disabled={isArchived}
                    onClick={() => onSave({ isUrgent: false })}
                    className={classNames(`${isArchived ? 'text-muted' : 'text-primary'}`)}
                    icon={{
                        name: 'bell-on',
                        color: isArchived ? 'muted' : 'primary'
                    }}
                />
                <MenuItem
                    label="Save & Notify as Urgent"
                    disabled={isArchived}
                    onClick={() => onSave({ isUrgent: true })}
                    className={classNames(`${isArchived ? 'text-muted' : 'text-danger'}`)}
                    icon={{
                        name: 'exclamation-triangle',
                        color: isArchived ? 'muted' : 'danger'
                    }}
                />
            </FloatingMenu>
        </div>
    )
}

export {
    DocumentEdit
}
